import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import {
  useCreateChecklistTemplateMutation,
  useDiscardChecklistTemplateDraftMutation,
  usePublishChecklistTemplateDraftMutation,
  useSaveChecklistTemplateDraftMutation,
  useGetChecklistTemplateQuery,
} from '@/services/checklistTemplates';
import { isErrorWithData } from '@/services/helpers';
import {
  PalmettoAutocomplete,
  PalmettoButton,
  PalmettoComboBox,
  PalmettoModal,
  PalmettoTextInput,
  PalmettoTextarea,
} from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useBlocker } from 'react-router-dom';

import {
  type ChecklistCondition,
  type ChecklistDecisionType,
  type ChecklistTemplateDocument,
  type Clientize,
  ProgramTypeLabels,
  QueueTypeLabel,
} from 'types';

import {
  ChecklistTemplateItemsEditor,
  type SectionFormValue,
  type TemplateItemFormValue,
} from './ChecklistTemplateItemsEditor';

const queueTypeOptions = Object.entries(QueueTypeLabel).map(([value, label]) => ({ value, label }));
const programTypeOptions = Object.entries(ProgramTypeLabels).map(([value, label]) => ({ value, label }));

interface TemplateFormValues {
  name: string;
  description: string;
  queueType: string;
  programTypes: string[];
  items: TemplateItemFormValue[];
  sections: SectionFormValue[];
  draftNotes: string;
}

export const ChecklistTemplateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: sourceId } = useParams<{ id: string }>();
  const isDraftEditMode = !!sourceId;
  const duplicateSource = !isDraftEditMode
    ? (location.state as { duplicateFrom?: Clientize<ChecklistTemplateDocument> })?.duplicateFrom
    : undefined;

  const [createTemplate, { isLoading: isCreating }] = useCreateChecklistTemplateMutation();
  const [saveDraft, { isLoading: isSavingDraft }] = useSaveChecklistTemplateDraftMutation();
  const [publishDraft, { isLoading: isPublishing }] = usePublishChecklistTemplateDraftMutation();
  const [discardDraft, { isLoading: isDiscarding }] = useDiscardChecklistTemplateDraftMutation();
  const { data: sourceTemplate, isLoading: isLoadingSource } = useGetChecklistTemplateQuery(sourceId ?? skipToken);

  // `sourceTemplate` is only fetched in draft-edit mode and `duplicateSource` only exists outside
  // it, so these two are the complete input set: neither `sourceId` nor `isDraftEditMode` can
  // change the result on its own. Recomputing resets the form (`values` below), so the dep list
  // has to stay this tight.
  const prefillValues = useMemo<TemplateFormValues | undefined>(() => {
    const source = sourceTemplate ?? duplicateSource;
    if (!source) return undefined;
    return {
      name: duplicateSource ? `Copy of ${source.name}` : source.name,
      description: source.description,
      queueType: source.queueType || '',
      programTypes: source.programTypes,
      sections: (source.sections ?? []).map((s) => ({
        key: s.key,
        title: s.title,
        order: s.order,
      })),
      items: source.items.map((item) => ({
        key: item.key,
        name: item.name,
        description: item.description,
        tooltipText: item.tooltipText,
        order: item.order,
        sectionKey: item.sectionKey,
        conditions: item.conditions,
        inputs: item.inputs,
        documentTypes: item.documentTypes,
        _stableId: crypto.randomUUID(),
      })),
      draftNotes: sourceTemplate?.draftNotes ?? '',
    };
  }, [sourceTemplate, duplicateSource]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty },
  } = useForm<TemplateFormValues>({
    defaultValues: {
      name: '',
      description: '',
      queueType: '',
      programTypes: [],
      items: [],
      sections: [],
      draftNotes: '',
    },
    values: prefillValues,
  });

  const intentionalNavigateRef = useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && !intentionalNavigateRef.current && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const watchedProgramTypes = useWatch({ control, name: 'programTypes' });
  const watchedSections = useWatch({ control, name: 'sections' });

  const [saveModalValues, setSaveModalValues] = useState<TemplateFormValues | null>(null);
  // A raw useState-driven value re-rendered inside PalmettoModal on every keystroke causes
  // Ark UI's FocusTrap to reactivate and reset focus to the modal's first tabbable element
  // (the close button) instead of the input. react-hook-form's Controller isolates re-renders
  // to just the changed field, so the modal itself never re-renders while typing.
  const {
    control: saveModalControl,
    handleSubmit: handleSaveModalSubmit,
    reset: resetSaveModalForm,
  } = useForm<{ description: string }>({ defaultValues: { description: '' } });
  const {
    control: publishModalControl,
    handleSubmit: handlePublishModalSubmit,
    reset: resetPublishModalForm,
  } = useForm<{ draftNotes: string }>({ defaultValues: { draftNotes: '' } });
  const [isItemEditing, setIsItemEditing] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const buildItemsAndSectionsPayload = useCallback((values: TemplateFormValues) => {
    const items = values.items.map((item) => ({
      key: item.key,
      name: item.name,
      description: item.description,
      ...(item.tooltipText ? { tooltipText: item.tooltipText } : {}),
      order: item.order,
      ...(item.sectionKey ? { sectionKey: item.sectionKey } : {}),
      ...(item.conditions && item.conditions.length > 0
        ? { conditions: item.conditions.map(({ _paramsRaw, ...c }) => c) }
        : {}),
      ...(item.inputs && item.inputs.length > 0
        ? {
            inputs: item.inputs.map(
              ({
                key,
                label,
                type,
                required,
                options,
                referenceKey,
                requirementFlagSettingId,
                conditions,
                tooltipText,
                evaluationKey,
                evaluationParams,
              }) => ({
                key,
                label,
                type,
                ...(required ? { required } : {}),
                ...(options && options.length > 0
                  ? {
                      options: options.map((o) => ({
                        ...o,
                        decisionType: o.decisionType as ChecklistDecisionType | undefined,
                      })),
                    }
                  : {}),
                ...(referenceKey ? { referenceKey } : {}),
                ...(requirementFlagSettingId ? { requirementFlagSettingId } : {}),
                ...(conditions && conditions.length > 0 ? { conditions: conditions as ChecklistCondition[] } : {}),
                ...(tooltipText ? { tooltipText } : {}),
                ...(evaluationKey ? { evaluationKey } : {}),
                ...(evaluationParams ? { evaluationParams } : {}),
              }),
            ),
          }
        : {}),
      ...(item.documentTypes && item.documentTypes.length > 0 ? { documentTypes: item.documentTypes } : {}),
    }));

    const sections = values.sections.map(({ key, title, order }) => ({ key, title, order }));

    return { items, sections };
  }, []);

  const doSave = useCallback(
    async (values: TemplateFormValues) => {
      try {
        const { items, sections } = buildItemsAndSectionsPayload(values);

        const savedTemplate = await createTemplate({
          name: values.name,
          description: values.description,
          ...(values.queueType ? { queueType: values.queueType } : {}),
          programTypes: values.programTypes,
          items,
          sections,
        }).unwrap();
        toast.success('Template created successfully');
        intentionalNavigateRef.current = true;
        navigate(`/admin/checklists/${savedTemplate.id}`);
        return true;
      } catch (error) {
        console.error('Error saving template:', error);
        if (isErrorWithData(error)) {
          toast.error(error.data.message);
        } else {
          toast.error('Failed to save template. Please try again.');
        }
        return false;
      }
    },
    [createTemplate, navigate, buildItemsAndSectionsPayload],
  );

  const buildSaveDraftPayload = useCallback(
    (values: TemplateFormValues, draftNotes: string) => {
      const { items, sections } = buildItemsAndSectionsPayload(values);
      return {
        name: values.name,
        description: values.description,
        programTypes: values.programTypes,
        items,
        sections,
        draftNotes,
      };
    },
    [buildItemsAndSectionsPayload],
  );

  const handleSaveDraft = useCallback(
    async (values: TemplateFormValues) => {
      if (!sourceId) return;
      try {
        await saveDraft({ id: sourceId, data: buildSaveDraftPayload(values, values.draftNotes) }).unwrap();
        toast.success('Draft saved');
      } catch (error) {
        console.error('Error saving draft:', error);
        if (isErrorWithData(error)) {
          toast.error(error.data.message);
        } else {
          toast.error('Error saving draft.');
        }
      }
    },
    [saveDraft, sourceId, buildSaveDraftPayload],
  );

  const onSubmit = useCallback(
    (values: TemplateFormValues) => {
      if (isDraftEditMode) {
        void handleSaveDraft(values);
        return;
      }
      setSaveModalValues(values);
      resetSaveModalForm();
    },
    [isDraftEditMode, handleSaveDraft, resetSaveModalForm],
  );

  const handleSaveModalClose = useCallback(() => {
    setSaveModalValues(null);
    resetSaveModalForm();
  }, [resetSaveModalForm]);

  const submitSaveModal = useCallback(
    async (data: { description: string }) => {
      if (!saveModalValues) return;
      const trimmed = data.description.trim();
      setValue('description', trimmed, { shouldDirty: true });
      const toSave = { ...saveModalValues, description: trimmed };
      const succeeded = await doSave(toSave);
      if (succeeded) {
        setSaveModalValues(null);
        resetSaveModalForm();
      }
    },
    [saveModalValues, setValue, doSave, resetSaveModalForm],
  );

  const handleSaveModalConfirm = useCallback(() => {
    void handleSaveModalSubmit(submitSaveModal)();
  }, [handleSaveModalSubmit, submitSaveModal]);

  const handleCancel = useCallback(() => {
    navigate('/admin/checklists');
  }, [navigate]);

  const handleQueueTypeChange = useCallback(
    (onChange: (v: string) => void) => (details: { value: string[] }) => {
      onChange(details.value[0] ?? '');
    },
    [],
  );

  const handleOpenPublishModal = useCallback(() => {
    resetPublishModalForm({ draftNotes: getValues('draftNotes') });
    setIsPublishModalOpen(true);
  }, [getValues, resetPublishModalForm]);

  const handleClosePublishModal = useCallback(() => {
    setIsPublishModalOpen(false);
  }, []);

  const submitPublishModal = useCallback(
    async (data: { draftNotes: string }) => {
      if (!sourceId) return;

      try {
        await saveDraft({ id: sourceId, data: buildSaveDraftPayload(getValues(), data.draftNotes) }).unwrap();
      } catch (error) {
        console.error('Error saving draft before publish:', error);
        if (isErrorWithData(error)) {
          toast.error(error.data.message);
        } else {
          toast.error('Error saving draft.');
        }
        return;
      }

      try {
        const published = await publishDraft({ id: sourceId, draftNotes: data.draftNotes }).unwrap();
        toast.success('Template published');
        setIsPublishModalOpen(false);
        intentionalNavigateRef.current = true;
        navigate(`/admin/checklists/${published.id}`);
      } catch (error) {
        console.error('Error publishing draft:', error);
        if (isErrorWithData(error)) {
          toast.error(error.data.message);
        } else {
          toast.error('Error publishing draft.');
        }
      }
    },
    [saveDraft, publishDraft, sourceId, navigate, getValues, buildSaveDraftPayload],
  );

  const handlePublishModalConfirm = useCallback(() => {
    void handlePublishModalSubmit(submitPublishModal)();
  }, [handlePublishModalSubmit, submitPublishModal]);

  const handleOpenDiscardModal = useCallback(() => {
    setIsDiscardModalOpen(true);
  }, []);

  const handleCloseDiscardModal = useCallback(() => {
    setIsDiscardModalOpen(false);
  }, []);

  const handleDiscardConfirm = useCallback(async () => {
    if (!sourceId) return;
    try {
      await discardDraft(sourceId).unwrap();
      toast.success('Draft discarded');
      setIsDiscardModalOpen(false);
      intentionalNavigateRef.current = true;
      navigate('/admin/checklists');
    } catch (error) {
      console.error('Error discarding draft:', error);
      if (isErrorWithData(error)) {
        toast.error(error.data.message);
      } else {
        toast.error('Error discarding draft.');
      }
    }
  }, [discardDraft, sourceId, navigate]);

  const handleBlockerReset = useCallback(() => blocker.reset?.(), [blocker]);
  const handleBlockerProceed = useCallback(() => blocker.proceed?.(), [blocker]);

  const isLoading = isCreating || isSavingDraft || isLoadingSource || isPublishing || isDiscarding;
  const pageTitle = isDraftEditMode
    ? `Edit Draft: ${sourceTemplate?.name || 'Loading...'}`
    : duplicateSource
      ? `Duplicate: ${duplicateSource.name}`
      : 'New Checklist Template';

  const hasEmptySections = useMemo(() => {
    if (!saveModalValues) return false;
    const itemSectionKeys = new Set(saveModalValues.items.map((i) => i.sectionKey).filter(Boolean));
    return saveModalValues.sections.some((s) => !itemSectionKeys.has(s.key));
  }, [saveModalValues]);

  return (
    <DsmPageContainer title={pageTitle}>
      <PageHeader title={pageTitle}>
        <div className="flex flex-col items-end gap-2">
          <div className="flex justify-end gap-4">
            {isDraftEditMode && (
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                onClick={handleOpenDiscardModal}
                disabled={isLoading}
              >
                Discard
              </PalmettoButton>
            )}
            <PalmettoButton
              type="button"
              emphasis="secondary"
              layout="hug"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </PalmettoButton>
            <PalmettoButton
              type="submit"
              form="checklist-template-form"
              emphasis={isDraftEditMode ? 'secondary' : 'primary'}
              layout="hug"
              disabled={isLoading || isItemEditing}
            >
              {isCreating || isSavingDraft ? 'Saving...' : isDraftEditMode ? 'Save Draft' : 'Save Template'}
            </PalmettoButton>
            {isDraftEditMode && (
              <PalmettoButton
                type="button"
                emphasis="primary"
                layout="hug"
                onClick={handleOpenPublishModal}
                disabled={isLoading || isItemEditing}
              >
                Publish
              </PalmettoButton>
            )}
          </div>
          {isItemEditing && (
            <p className="text-sm text-text-secondary">
              Save or cancel the item being edited before saving the template.
            </p>
          )}
        </div>
      </PageHeader>
      <form id="checklist-template-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-6 grid grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field, fieldState }) => (
                <PalmettoTextInput
                  id="name"
                  label="Template Name"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  validationMessage={
                    fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
                  }
                  required
                  disabled={isLoading}
                />
              )}
            />
          </div>

          <div>
            {isDraftEditMode ? (
              <PalmettoTextInput
                id="current-description"
                label="Description"
                value={sourceTemplate?.description ?? ''}
                onChange={() => undefined}
                disabled
              />
            ) : (
              <p className="text-sm text-text-secondary">
                A description will be requested when you save this template.
              </p>
            )}
          </div>
          <div>
            {isDraftEditMode && (
              <Controller
                name="draftNotes"
                control={control}
                render={({ field }) => (
                  <PalmettoTextarea
                    id="draft-notes"
                    label="Draft Notes"
                    placeholder="Notes about this draft"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    rows={2}
                    disabled={isLoading}
                  />
                )}
              />
            )}
          </div>

          <div>
            <Controller
              name="queueType"
              control={control}
              render={({ field }) => (
                <PalmettoAutocomplete
                  key={sourceTemplate ? 'loaded' : 'loading'}
                  label="Queue Type"
                  helperText="Select Queues which should trigger this Checklist creation upon submission"
                  items={[
                    { label: 'None', value: '', selected: !field.value },
                    ...queueTypeOptions
                      .map((opt) => ({ ...opt, selected: opt.value === field.value }))
                      .toSorted((a, b) => a.label.localeCompare(b.label)),
                  ]}
                  onValueChange={handleQueueTypeChange(field.onChange)}
                  disabled={isDraftEditMode || isLoading}
                  maxMenuHeight={560}
                />
              )}
            />
          </div>
          <div>
            <Controller
              name="programTypes"
              control={control}
              render={({ field }) => (
                <PalmettoComboBox
                  id="programTypes"
                  label="Program Types"
                  helperText="Leave empty to apply to all Program Types, or select which Program Types this Checklist applies to"
                  items={programTypeOptions
                    .map((opt) => ({
                      ...opt,
                      selected: field.value.includes(opt.value),
                    }))
                    .toSorted((a, b) => a.label.localeCompare(b.label))}
                  onValueChange={(details) => field.onChange(details.value)}
                  disabled={isLoading}
                />
              )}
            />
          </div>
        </div>

        <div className="mb-6">
          <Controller
            name="items"
            control={control}
            rules={{ validate: (v) => (v && v.length > 0) || 'At least one item is required' }}
            render={({ field, fieldState }) => (
              <>
                <Controller
                  name="sections"
                  control={control}
                  render={({ field: sectionsField }) => (
                    <ChecklistTemplateItemsEditor
                      items={field.value}
                      onChange={field.onChange}
                      programTypes={watchedProgramTypes}
                      sections={watchedSections}
                      onSectionsChange={sectionsField.onChange}
                      onEditingChange={setIsItemEditing}
                    />
                  )}
                />
                {fieldState.error && <div className="mt-1 text-sm text-text-error">{fieldState.error.message}</div>}
              </>
            )}
          />
        </div>
      </form>

      <PalmettoModal
        open={!!saveModalValues}
        onClose={handleSaveModalClose}
        invertedBackground={false}
        bottomContent={
          <div className="flex justify-end gap-4">
            <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleSaveModalClose}>
              Cancel
            </PalmettoButton>
            <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleSaveModalConfirm}>
              Save Template
            </PalmettoButton>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <h2>Save Template</h2>
          {hasEmptySections && (
            <p className="text-text-secondary">
              One or more sections have no items. Empty sections will be saved but won&apos;t appear on checklists.
            </p>
          )}
          <Controller
            name="description"
            control={saveModalControl}
            rules={{ validate: (v) => (v && v.trim().length > 0) || 'Description is required' }}
            render={({ field, fieldState }) => (
              <PalmettoTextarea
                id="save-modal-description"
                label="Description"
                placeholder="Describe this checklist template"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                validationMessage={
                  fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
                }
                rows={3}
                autoFocus
              />
            )}
          />
        </div>
      </PalmettoModal>

      <PalmettoModal
        open={isPublishModalOpen}
        onClose={handleClosePublishModal}
        invertedBackground={false}
        bottomContent={
          <div className="flex justify-end gap-8">
            <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleClosePublishModal}>
              Cancel
            </PalmettoButton>
            <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handlePublishModalConfirm}>
              Publish
            </PalmettoButton>
          </div>
        }
      >
        <div className="flex flex-col gap-16">
          <h2>Publish this draft?</h2>
          <p className="text-text-secondary">This draft will replace the current active template once published.</p>
          <Controller
            name="draftNotes"
            control={publishModalControl}
            render={({ field }) => (
              <PalmettoTextarea
                id="publish-modal-draft-notes"
                label="Draft Notes"
                placeholder="Notes about this draft"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                rows={3}
              />
            )}
          />
        </div>
      </PalmettoModal>

      {isDiscardModalOpen && (
        <PalmettoModal
          open
          onClose={handleCloseDiscardModal}
          invertedBackground={false}
          bottomContent={
            <div className="flex justify-end gap-8">
              <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleCloseDiscardModal}>
                Cancel
              </PalmettoButton>
              <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleDiscardConfirm}>
                Discard
              </PalmettoButton>
            </div>
          }
        >
          <div className="flex flex-col gap-8">
            <h2>Discard this draft?</h2>
            <p className="text-text-secondary">This can&apos;t be undone.</p>
          </div>
        </PalmettoModal>
      )}

      <PalmettoModal open={blocker.state === 'blocked'} onClose={handleBlockerReset}>
        <p>You have unsaved changes. Are you sure you want to leave?</p>
        <div className="mt-16 flex justify-end gap-8">
          <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleBlockerReset}>
            Stay
          </PalmettoButton>
          <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleBlockerProceed}>
            Leave anyway
          </PalmettoButton>
        </div>
      </PalmettoModal>
    </DsmPageContainer>
  );
};
